import { motion } from "framer-motion";
import { PawPrint, GlassWater, Trees, Egg, Drumstick, Milk } from "lucide-react";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import { RESERVATION_URL } from "../data/content";

const POLICIES = [
    { icon: Trees, title: "Garden seating for all", text: "Pets dine with you across our open-air garden, cabanas and tree-lined corners.", testid: "pet-policy-garden" },
    { icon: GlassWater, title: "Water bowls, always", text: "Fresh water bowls arrive at your table the moment your furry friend settles in.", testid: "pet-policy-water" },
    { icon: Drumstick, title: "A menu made for them", text: "Our kitchen plates up a dedicated dog menu, simple, safe and tail-waggingly good.", testid: "pet-policy-menu" },
    { icon: PawPrint, title: "Happy pets, happy tables", text: "Keep them leashed and friendly, and our team will handle the belly rubs.", testid: "pet-policy-leash" },
];

const PET_MENU = [
    { name: "Boiled Eggs", desc: "Two soft, simple boiled eggs, no salt, no spice, all love", price: 99, icon: Egg },
    { name: "Fresh Paneer", desc: "100 gms of soft, fresh paneer, gentle on little tummies", price: 149, icon: Milk },
    { name: "Shredded Chicken", desc: "250 gms of plain, slow-cooked shredded chicken", price: 249, icon: Drumstick },
];

export default function PetsPage() {
    return (
        <>
            <Navbar />
            <main className="pt-20">
                <section className="relative py-24 md:py-32 px-6 md:px-10 overflow-hidden" style={{ backgroundColor: "#F8F6F0" }} data-testid="pets-hero">
                    <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full opacity-40 blur-3xl" style={{ backgroundColor: "#C9E8DD" }} />
                    <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
                        <motion.div initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                            <p className="font-serif-brand italic text-xl text-[#00958C] mb-4">Because family includes the four-legged kind</p>
                            <h1 className="text-4xl md:text-6xl font-bold tracking-tighter text-[#262055] leading-[1.02]" data-testid="pets-headline">
                                Pets are family<br />at the Avenue.
                            </h1>
                            <p className="mt-8 text-base md:text-lg leading-relaxed text-[#262055]/70 max-w-xl" data-testid="pets-intro">
                                Bring your dogs along for lunch under the trees, they get their own
                                menu, fresh water and plenty of shade while you linger over yours.
                            </p>
                            <div className="mt-10 flex flex-wrap gap-4">
                                <a href={RESERVATION_URL} target="_blank" rel="noopener noreferrer" className="rounded-full bg-[#F0841B] text-white font-semibold px-9 py-4 shadow-xl shadow-[#F0841B]/25 hover:brightness-110 hover:-translate-y-0.5 transition-[transform,filter] duration-200" data-testid="pets-reserve-button">
                                    Book a Pet-Friendly Table
                                </a>
                                <a href="#pet-menu" className="rounded-full border-2 border-[#262055]/20 text-[#262055] font-semibold px-9 py-4 hover:bg-white transition-colors duration-200" data-testid="pets-menu-button">
                                    See the Pet Menu
                                </a>
                            </div>
                        </motion.div>
                        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}>
                            <img src="/gallery/DSC05772.webp" alt="A cosy daybed cabana where pets lounge with their humans" className="rounded-[2.5rem] w-full h-[380px] md:h-[480px] object-cover shadow-2xl shadow-[#262055]/15" data-testid="pets-hero-image" />
                        </motion.div>
                    </div>
                </section>

                <section className="py-20 md:py-28 px-6 md:px-10" data-testid="pets-policy-section">
                    <div className="max-w-7xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-bold tracking-tighter text-[#262055] mb-12" data-testid="pets-policy-headline">Our pet promise</h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {POLICIES.map((p, i) => (
                                <motion.div
                                    key={p.title}
                                    initial={{ opacity: 0, y: 28 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{ duration: 0.55, delay: i * 0.1 }}
                                    className="bg-white rounded-3xl p-8 shadow-lg shadow-[#262055]/5 hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300"
                                    data-testid={p.testid}
                                >
                                    <div className="w-12 h-12 rounded-2xl bg-[#00958C]/10 flex items-center justify-center mb-6">
                                        <p.icon size={22} className="text-[#00958C]" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-[#262055] mb-2">{p.title}</h3>
                                    <p className="text-sm leading-relaxed text-[#262055]/65">{p.text}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="py-20 md:py-28 px-6 md:px-10" style={{ backgroundColor: "#C9E8DD" }} data-testid="pets-gallery-section">
                    <div className="max-w-7xl mx-auto">
                        <motion.div initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7 }}>
                            <p className="font-serif-brand italic text-xl text-[#262055]/70 mb-3">Regulars with wagging tails</p>
                            <h2 className="text-3xl md:text-4xl font-bold tracking-tighter text-[#262055]" data-testid="pets-gallery-headline">Meet our furry guests</h2>
                        </motion.div>
                        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl" data-testid="pets-gallery-grid">
                            {[
                                { src: "/pets/pet-shihtzu.webp", alt: "A Shih Tzu in a red harness at Uncover Avenue", testid: "pets-gallery-shihtzu" },
                                { src: "/pets/pet-beagle.webp", alt: "A beagle on a leash at Uncover Avenue", testid: "pets-gallery-beagle" },
                                { src: "/pets/pet-pigtails.webp", alt: "A Shih Tzu with pigtails in a blue jacket at Esak", testid: "pets-gallery-pigtails" },
                                { src: "/pets/pet-pink.webp", alt: "A Shih Tzu in a pink outfit at Esak", testid: "pets-gallery-pink" },
                            ].map((p, i) => (
                                <motion.div
                                    key={p.testid}
                                    initial={{ opacity: 0, y: 28 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-40px" }}
                                    transition={{ duration: 0.55, delay: i * 0.12 }}
                                    className="rounded-[2rem] overflow-hidden shadow-xl shadow-[#262055]/10 hover:-translate-y-1 hover:shadow-2xl transition-[transform,box-shadow] duration-300"
                                    data-testid={p.testid}
                                >
                                    <img src={p.src} alt={p.alt} className="w-full aspect-[2/3] object-cover" loading="lazy" />
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                <section id="pet-menu" className="py-20 md:py-28 px-6 md:px-10" style={{ backgroundColor: "#FFF1DC" }} data-testid="pet-menu-section">
                    <div className="max-w-4xl mx-auto">
                        <motion.div initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7 }} className="text-center">
                            <p className="font-serif-brand italic text-xl mb-3" style={{ color: "#E50046" }}>Flavours for the modern heart, and paws</p>
                            <h2 className="font-syne text-4xl md:text-5xl font-extrabold tracking-tight text-[#1C1C1E]" data-testid="pet-menu-headline">The Dog Menu</h2>
                            <p className="mt-4 text-base text-[#1C1C1E]/60" data-testid="pet-menu-subcopy">
                                Simple, safe and served with the same love as everything else at Esak.
                            </p>
                        </motion.div>

                        <div className="mt-12 grid sm:grid-cols-3 gap-6" data-testid="pet-menu-grid">
                            {PET_MENU.map((item, i) => (
                                <motion.div
                                    key={item.name}
                                    initial={{ opacity: 0, y: 28 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-40px" }}
                                    transition={{ duration: 0.55, delay: i * 0.1 }}
                                    className="bg-white rounded-3xl p-8 text-center shadow-lg shadow-[#1C1C1E]/5 hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300"
                                    data-testid={`pet-menu-item-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    <div className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: "#E5004612" }}>
                                        <item.icon size={26} style={{ color: "#E50046" }} />
                                    </div>
                                    <h3 className="font-syne text-lg font-bold text-[#1C1C1E]">{item.name}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-[#1C1C1E]/55">{item.desc}</p>
                                    <p className="mt-4 font-serif-brand text-xl font-semibold" style={{ color: "#E50046" }}>₹{item.price}</p>
                                </motion.div>
                            ))}
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.6 }}
                            className="mt-14"
                            data-testid="pet-menu-posters"
                        >
                            <p className="text-center text-xs font-bold uppercase tracking-widest text-[#1C1C1E]/45 mb-5">As announced on @esak.delhi</p>
                            <div className="grid sm:grid-cols-2 gap-6">
                                {[
                                    { src: "/pets/pet-menu-poster-2.webp", alt: "Esak Dog Food Menu announcement poster", testid: "pet-poster-intro" },
                                    { src: "/pets/pet-menu-poster-1.webp", alt: "Esak pet menu with prices, Boiled Eggs, Paneer, Shredded Chicken", testid: "pet-poster-menu" },
                                ].map((p) => (
                                    <a
                                        key={p.testid}
                                        href="https://www.instagram.com/p/DXWjNeNmt5n/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="rounded-3xl overflow-hidden shadow-lg shadow-[#1C1C1E]/10 hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300 block"
                                        data-testid={p.testid}
                                    >
                                        <img src={p.src} alt={p.alt} className="w-full object-cover" loading="lazy" />
                                    </a>
                                ))}
                            </div>
                        </motion.div>

                        <p className="mt-12 text-center text-sm text-[#1C1C1E]/50" data-testid="pet-menu-note">
                            Served plain with no salt, onion, garlic or seasoning. Cats are welcome too, our team will happily portion any of these for them.
                        </p>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
